/**
 * The M2 HUD view (plan §6 M2-19): a DOM/CSS overlay mounted once inside `#hud`, next to the M1 combat HUD. Per
 * frame it only writes `transform`, `opacity`, `textContent`, one SVG `stroke-dashoffset` per ring and `data-*`
 * attributes on cached elements, and it never reads layout. Every value is diffed against the last value written
 * (quantised to what is visible), so a steady HUD writes nothing.
 *
 * `#hud` gets `data-mode="career"` at mount (m2hud.css hides the M1 wave block and the M1 respawn line in career
 * mode) and `data-ui="open"` while a UI screen is up (the whole HUD, M1 and M2, hides behind the station screens).
 *
 * Colour is never the only cue (style guide §9.5): coolant anchors are diamonds, not rings; over-limit speed gets a
 * chevron and a heavier frame; the band of the power needle is also written as a data attribute shape change.
 *
 * Allocation note (D-014): strings for DOM writes are built only when a quantised value changes, the unavoidable
 * cost of writing the DOM; a still HUD allocates nothing.
 */
import { CONTRACT_TYPES } from '../../data/m2Schemas'
import type { M2HudTuning } from '../../features/m2hud/tuning'
import { NAV, SALVAGE_STATUS, TOOL } from '../../space/contracts'
import { cruiseEtaKey, formatCruiseEta, formatHudDistance, hudDistanceKey } from '../reach/format'
import { hudStatus, statusHide, statusShow, type StatusStack } from '../status'
import { ANCHOR_MARKERS, contractMarkerLabel, DOCK_PHASE, DOCK_PROMPT, PART_BRACKETS, SCAN_KIND, SCAN_ROWS, STATION_STATE, type M2HudState, type M2Marker } from './model'

/** The subset of `CSSStyleDeclaration` the view writes. */
export interface M2Style {
  transform: string
  opacity: string
  strokeDashoffset: string
}

/** The subset of an element the view writes (tests pass fakes that count writes). */
export interface M2Node {
  textContent: string | null
  readonly style: M2Style
  setAttribute(name: string, value: string): void
}

export interface M2MarkerEls {
  root: M2Node
  label: M2Node
  distance: M2Node
}

export interface M2PartEls {
  root: M2Node
  frame: M2Node
  value: M2Node
  condition: M2Node
}

export interface M2RowEls {
  root: M2Node
  label: M2Node
  value: M2Node
}

export interface M2ToastEls {
  root: M2Node
  speaker: M2Node
  text: M2Node
}

/** Every element the view writes. */
export interface M2HudElements {
  /** `#hud` (data-mode, data-ui). */
  host: M2Node
  /** `.m2hud-root` (data-visible, data-mode, data-alive). */
  root: M2Node
  mode: M2Node
  cargo: M2Node
  wallet: M2Node
  lease: M2Node
  collections: M2Node
  nav: M2Node
  navFrame: M2Node
  navDistance: M2Node
  arrow: M2Node
  arrowHead: M2Node
  arrowDistance: M2Node
  stations: M2MarkerEls[]
  contracts: M2MarkerEls[]
  scan: M2Node
  scanTitle: M2Node
  scanProgress: M2Node
  scanValue: M2Node
  scanMode: M2Node
  scanTier: M2Node
  scanField: M2Node
  scanRows: M2RowEls[]
  mining: M2Node
  miningMode: M2Node
  fracture: M2Node
  window: M2Node
  needle: M2Node
  mkLine: M2Node
  instability: M2Node
  progress: M2Node
  heatBar: M2Node
  heat: M2Node
  miningNote: M2Node
  anchors: M2Node[]
  anchorArcs: M2Node[]
  parts: M2PartEls[]
  salvage: M2Node
  salvageStatus: M2Node
  cut: M2Node
  tractor: M2Node
  dock: M2Node
  dockPhase: M2Node
  clearance: M2Node
  speedRow: M2Node
  speed: M2Node
  speedLimit: M2Node
  padDistance: M2Node
  dockPrompt: M2Node
  reticle: M2Node
  rollBar: M2Node
  holdArc: M2Node
  nose: M2Node
  pad: M2Node
  toasts: M2ToastEls[]
}

export interface M2HudCounts {
  stations: number
  contracts: number
  toasts: number
}

/** Builds the element structure from a node factory (the DOM mount and the tests share it). Init only. */
export function buildM2HudElements(host: M2Node, make: (name: string) => M2Node, counts: M2HudCounts): M2HudElements {
  const marker = (prefix: string, i: number): M2MarkerEls => ({
    root: make(`${prefix}${i}`),
    label: make(`${prefix}${i}.label`),
    distance: make(`${prefix}${i}.distance`),
  })
  const list = <T>(n: number, f: (i: number) => T): T[] => Array.from({ length: n }, (_, i) => f(i))
  return {
    host,
    root: make('root'),
    mode: make('mode'),
    cargo: make('cargo'),
    wallet: make('wallet'),
    lease: make('lease'),
    collections: make('collections'),
    nav: make('nav'),
    navFrame: make('nav.frame'),
    navDistance: make('nav.distance'),
    arrow: make('arrow'),
    arrowHead: make('arrow.head'),
    arrowDistance: make('arrow.distance'),
    stations: list(counts.stations, (i) => marker('station', i)),
    contracts: list(counts.contracts, (i) => marker('contract', i)),
    scan: make('scan'),
    scanTitle: make('scan.title'),
    scanProgress: make('scan.progress'),
    scanValue: make('scan.value'),
    scanMode: make('scan.mode'),
    scanTier: make('scan.tier'),
    scanField: make('scan.field'),
    scanRows: list(SCAN_ROWS, (i) => ({ root: make(`row${i}`), label: make(`row${i}.label`), value: make(`row${i}.value`) })),
    mining: make('mining'),
    miningMode: make('mining.mode'),
    fracture: make('fracture'),
    window: make('fracture.window'),
    needle: make('fracture.needle'),
    mkLine: make('fracture.mk'),
    instability: make('fracture.instability'),
    progress: make('fracture.progress'),
    heatBar: make('heat'),
    heat: make('heat.fill'),
    miningNote: make('mining.note'),
    anchors: list(ANCHOR_MARKERS, (i) => make(`anchor${i}`)),
    anchorArcs: list(ANCHOR_MARKERS, (i) => make(`anchor${i}.arc`)),
    parts: list(PART_BRACKETS, (i) => ({
      root: make(`part${i}`),
      frame: make(`part${i}.frame`),
      value: make(`part${i}.value`),
      condition: make(`part${i}.condition`),
    })),
    salvage: make('salvage'),
    salvageStatus: make('salvage.status'),
    cut: make('salvage.cut'),
    tractor: make('salvage.tractor'),
    dock: make('dock'),
    dockPhase: make('dock.phase'),
    clearance: make('dock.clearance'),
    speedRow: make('dock.speedrow'),
    speed: make('dock.speed'),
    speedLimit: make('dock.limit'),
    padDistance: make('dock.pad'),
    dockPrompt: make('dock.prompt'),
    reticle: make('reticle'),
    rollBar: make('reticle.roll'),
    holdArc: make('reticle.hold'),
    nose: make('nose'),
    pad: make('pad'),
    toasts: list(counts.toasts, (i) => ({ root: make(`toast${i}`), speaker: make(`toast${i}.speaker`), text: make(`toast${i}.text`) })),
  }
}

// ---- Channels ---------------------------------------------------------------------------------------------------

/** One diffed write target: a node plus the last values written to one of its properties. */
interface Channel {
  node: M2Node
  a: number
  b: number
  c: number
  s: string
}

function ch(node: M2Node): Channel {
  return { node, a: NaN, b: NaN, c: NaN, s: '\u0000' }
}

interface MarkerChannels {
  on: Channel
  edge: Channel
  /** Label flipped left of the icon (right edge) and label text shown (declutter); W1 polish. */
  flip: Channel
  full: Channel
  state: Channel
  pos: Channel
  label: Channel
  distance: Channel
}

interface PartChannels {
  on: Channel
  state: Channel
  pos: Channel
  frame: Channel
  value: Channel
  condition: Channel
}

interface RowChannels {
  on: Channel
  flag: Channel
  label: Channel
  value: Channel
}

interface ToastChannels {
  on: Channel
  kind: Channel
  opacity: Channel
  seq: Channel
  speaker: Channel
  text: Channel
}

export interface M2HudView {
  el: M2HudElements
  /** The top-centre status stack the toasts show in (H11). */
  status: StatusStack
  hostMode: Channel
  hostUi: Channel
  visible: Channel
  mode: Channel
  alive: Channel
  modeText: Channel
  cargo: Channel
  cargoFull: Channel
  wallet: Channel
  lease: Channel
  leaseWarn: Channel
  collections: Channel
  collectionsText: Channel
  navOn: Channel
  navPos: Channel
  navFrame: Channel
  navDistance: Channel
  arrowOn: Channel
  arrowPos: Channel
  arrowHead: Channel
  arrowDistance: Channel
  stations: MarkerChannels[]
  contracts: MarkerChannels[]
  scanOn: Channel
  scanState: Channel
  scanTitle: Channel
  scanProgress: Channel
  scanValue: Channel
  scanMode: Channel
  scanTier: Channel
  scanField: Channel
  rows: RowChannels[]
  miningOn: Channel
  miningMode: Channel
  fractureOn: Channel
  window: Channel
  needle: Channel
  needleBand: Channel
  mkLine: Channel
  instability: Channel
  progress: Channel
  heat: Channel
  heatState: Channel
  miningNote: Channel
  miningNoteOn: Channel
  anchorOn: Channel[]
  anchorPos: Channel[]
  anchorKind: Channel[]
  anchorArc: Channel[]
  parts: PartChannels[]
  salvageOn: Channel
  salvageStatus: Channel
  salvageStatusKind: Channel
  cut: Channel
  cutOn: Channel
  tractorOn: Channel
  dockOn: Channel
  dockPhase: Channel
  dockPhaseText: Channel
  clearance: Channel
  speedOver: Channel
  speed: Channel
  speedLimit: Channel
  padDistance: Channel
  promptOn: Channel
  promptRange: Channel
  promptText: Channel
  reticleOn: Channel
  reticleAligned: Channel
  rollBar: Channel
  holdArc: Channel
  noseOn: Channel
  nosePos: Channel
  padOn: Channel
  padPos: Channel
  toasts: ToastChannels[]
}

export function createM2HudView(el: M2HudElements, status: StatusStack = hudStatus): M2HudView {
  const markers = (list: M2MarkerEls[]): MarkerChannels[] =>
    list.map((m) => ({ on: ch(m.root), edge: ch(m.root), flip: ch(m.root), full: ch(m.root), state: ch(m.root), pos: ch(m.root), label: ch(m.label), distance: ch(m.distance) }))
  return {
    el,
    status,
    hostMode: ch(el.host),
    hostUi: ch(el.host),
    visible: ch(el.root),
    mode: ch(el.root),
    alive: ch(el.root),
    modeText: ch(el.mode),
    cargo: ch(el.cargo),
    cargoFull: ch(el.cargo),
    wallet: ch(el.wallet),
    lease: ch(el.lease),
    leaseWarn: ch(el.lease),
    collections: ch(el.collections),
    collectionsText: ch(el.collections),
    navOn: ch(el.nav),
    navPos: ch(el.nav),
    navFrame: ch(el.navFrame),
    navDistance: ch(el.navDistance),
    arrowOn: ch(el.arrow),
    arrowPos: ch(el.arrow),
    arrowHead: ch(el.arrowHead),
    arrowDistance: ch(el.arrowDistance),
    stations: markers(el.stations),
    contracts: markers(el.contracts),
    scanOn: ch(el.scan),
    scanState: ch(el.scan),
    scanTitle: ch(el.scanTitle),
    scanProgress: ch(el.scanProgress),
    scanValue: ch(el.scanValue),
    scanMode: ch(el.scanMode),
    scanTier: ch(el.scanTier),
    scanField: ch(el.scanField),
    rows: el.scanRows.map((r) => ({ on: ch(r.root), flag: ch(r.root), label: ch(r.label), value: ch(r.value) })),
    miningOn: ch(el.mining),
    miningMode: ch(el.miningMode),
    fractureOn: ch(el.fracture),
    window: ch(el.window),
    needle: ch(el.needle),
    needleBand: ch(el.needle),
    mkLine: ch(el.mkLine),
    instability: ch(el.instability),
    progress: ch(el.progress),
    heat: ch(el.heat),
    heatState: ch(el.heatBar),
    miningNote: ch(el.miningNote),
    miningNoteOn: ch(el.miningNote),
    anchorOn: el.anchors.map(ch),
    anchorPos: el.anchors.map(ch),
    anchorKind: el.anchors.map(ch),
    anchorArc: el.anchorArcs.map(ch),
    parts: el.parts.map((p) => ({ on: ch(p.root), state: ch(p.root), pos: ch(p.root), frame: ch(p.frame), value: ch(p.value), condition: ch(p.condition) })),
    salvageOn: ch(el.salvage),
    salvageStatus: ch(el.salvageStatus),
    salvageStatusKind: ch(el.salvageStatus),
    cut: ch(el.cut),
    cutOn: ch(el.salvage),
    tractorOn: ch(el.tractor),
    dockOn: ch(el.dock),
    dockPhase: ch(el.dock),
    dockPhaseText: ch(el.dockPhase),
    clearance: ch(el.clearance),
    speedOver: ch(el.speedRow),
    speed: ch(el.speed),
    speedLimit: ch(el.speedLimit),
    padDistance: ch(el.padDistance),
    promptOn: ch(el.dockPrompt),
    promptRange: ch(el.dockPrompt),
    promptText: ch(el.dockPrompt),
    reticleOn: ch(el.reticle),
    reticleAligned: ch(el.reticle),
    rollBar: ch(el.rollBar),
    holdArc: ch(el.holdArc),
    noseOn: ch(el.nose),
    nosePos: ch(el.nose),
    padOn: ch(el.pad),
    padPos: ch(el.pad),
    toasts: el.toasts.map((t) => ({ on: ch(t.root), kind: ch(t.root), opacity: ch(t.root), seq: ch(t.text), speaker: ch(t.speaker), text: ch(t.text) })),
  }
}

// ---- Diffed writers ---------------------------------------------------------------------------------------------

function q10(v: number): number {
  return Math.round(v * 10) / 10
}

function translate(c: Channel, x: number, y: number): void {
  const qx = q10(x)
  const qy = q10(y)
  if (qx === c.a && qy === c.b) return
  c.a = qx
  c.b = qy
  c.node.style.transform = `translate3d(${qx}px,${qy}px,0)`
}

function rotate(c: Channel, deg: number): void {
  const qd = q10(deg)
  if (qd === c.a) return
  c.a = qd
  c.node.style.transform = `rotate(${qd}deg)`
}

function scale(c: Channel, s: number): void {
  const qs = Math.round(s * 100) / 100
  if (qs === c.a) return
  c.a = qs
  c.node.style.transform = `scale(${qs})`
}

/** A 0..1 bar fill (origin left). */
function scaleX(c: Channel, s: number): void {
  const qs = Math.round((s < 0 ? 0 : s > 1 ? 1 : s) * 500) / 500
  if (qs === c.a) return
  c.a = qs
  c.node.style.transform = `scaleX(${qs})`
}

/** A full-width element moved along its track by `at` (0..1 of the track) and scaled to `width` of it. */
function span(c: Channel, at: number, width: number): void {
  const qa = Math.round(at * 1000) / 10
  const qw = Math.round(width * 1000) / 1000
  if (qa === c.a && qw === c.b) return
  c.a = qa
  c.b = qw
  c.node.style.transform = `translateX(${qa}%) scaleX(${qw})`
}

/** A full-width track marker (a line at its left edge) moved to `at` (0..1 of the track). */
function slide(c: Channel, at: number): void {
  const qa = Math.round((at < 0 ? 0 : at > 1 ? 1 : at) * 1000) / 10
  if (qa === c.a) return
  c.a = qa
  c.node.style.transform = `translateX(${qa}%)`
}

function opacity(c: Channel, v: number): void {
  const qv = Math.round((v < 0 ? 0 : v > 1 ? 1 : v) * 100) / 100
  if (qv === c.a) return
  c.a = qv
  c.node.style.opacity = String(qv)
}

/** A ring's arc: `pathLength` 100 in the markup, so the offset is 100 × (1 − fraction). */
function arc(c: Channel, fraction: number): void {
  const qv = Math.round((1 - (fraction < 0 ? 0 : fraction > 1 ? 1 : fraction)) * 200) / 2
  if (qv === c.a) return
  c.a = qv
  c.node.style.strokeDashoffset = String(qv)
}

/** A text constant (tuning labels and model strings: compared by identity, no string built per frame). */
function text(c: Channel, s: string): void {
  if (s === c.s && c.c !== 1) return
  c.s = s
  c.c = NaN
  c.node.textContent = s
}

function integer(c: Channel, v: number): void {
  const n = Math.round(v)
  if (n === c.a) return
  c.a = n
  c.node.textContent = String(n)
}

/** Metres to 5 m below 1 km, kilometres to 0.1 km below 1,000 km, then Mm and Gm (D-459 bug 10). */
function distance(c: Channel, metres: number): void {
  const key = hudDistanceKey(metres)
  if (key === c.a) return
  c.a = key
  c.node.textContent = formatHudDistance(metres)
}

/**
 * A far marker's distance and cruise time (CL-8): "40.9 Gm · ~12 s cruise", or the distance alone ("1.4 km") with no
 * cruise time. Diffs the distance key (`c.a`) and the cruise key (`c.b`): the text is built only when either changes.
 */
function cruiseDistance(c: Channel, metres: number, cruiseS: number, tuning: M2HudTuning): void {
  const e = tuning.cruiseEta
  const key = hudDistanceKey(metres)
  const eta = cruiseEtaKey(cruiseS, e.minutesFromS)
  if (key === c.a && eta === c.b) return
  c.a = key
  c.b = eta
  const d = formatHudDistance(metres)
  c.node.textContent = eta === 0 ? d : d + e.separator + formatCruiseEta(cruiseS, e.minutesFromS, e.seconds, e.minutes)
}

/** Minutes and seconds ("34:07") after a constant prefix. Shares a channel with `text` (`c.c` = 1 marks clock mode). */
function clock(c: Channel, seconds: number, prefix: string): void {
  const n = Math.ceil(seconds)
  if (n === c.a && c.c === 1 && prefix === c.s) return
  c.a = n
  c.c = 1
  c.s = prefix
  const m = Math.floor(n / 60)
  const s = n - m * 60
  c.node.textContent = `${prefix}${m}:${s < 10 ? '0' : ''}${s}`
}

function credits(c: Channel, v: number): void {
  const n = Math.round(v)
  if (n === c.a) return
  c.a = n
  c.node.textContent = `${n.toLocaleString('en-US')} cr`
}

function cargo(c: Channel, used: number, capacity: number): void {
  const qu = Math.round(used * 10)
  const qc = Math.round(capacity * 10)
  if (qu === c.a && qc === c.b) return
  c.a = qu
  c.b = qc
  c.node.textContent = `${(qu / 10).toFixed(1)} / ${Math.round(capacity)} t`
}

function partValue(c: Channel, known: boolean, low: number, high: number, unknown: string): void {
  const ql = known ? Math.round(low) : -1
  const qh = known ? Math.round(high) : -1
  if (ql === c.a && qh === c.b) return
  c.a = ql
  c.b = qh
  c.node.textContent = !known ? unknown : ql === qh ? `${ql.toLocaleString('en-US')} cr` : `${ql.toLocaleString('en-US')}–${qh.toLocaleString('en-US')} cr`
}

function percentText(c: Channel, v: number): void {
  const n = Math.round(v * 100)
  if (n === c.a) return
  c.a = n
  c.node.textContent = `${n}%`
}

function attr(c: Channel, name: string, value: string): void {
  if (value === c.s) return
  c.s = value
  c.node.setAttribute(name, value)
}

function flag(c: Channel, name: string, on: boolean): void {
  attr(c, name, on ? '1' : '0')
}

const MODE_NAMES = ['combat', 'mining', 'salvage'] as const
const STATION_STATE_NAMES = ['none', 'cleared', 'autodock', 'docked'] as const
const DOCK_PHASE_NAMES = ['space', 'cleared', 'autodock', 'docked'] as const
const BAND_NAMES = ['below', 'inside', 'above'] as const
const SALVAGE_STATUS_NAMES = ['ok', 'toolarge', 'holdfull', 'range', 'claimed'] as const
const PART_STATE_NAMES = ['attached', 'freed', 'tractored'] as const
const SCAN_KIND_NAMES = ['none', 'rock', 'wreck'] as const
const RAD_TO_DEG = 180 / Math.PI

// ---- Frame ------------------------------------------------------------------------------------------------------

/** Writes `s` into the DOM through the diffed channels. Allocation-free while values are steady. */
export function renderM2Hud(v: M2HudView, s: M2HudState, tuning: M2HudTuning): void {
  attr(v.hostMode, 'data-mode', 'career')
  attr(v.hostUi, 'data-ui', s.uiOpen ? 'open' : 'closed')
  flag(v.visible, 'data-visible', s.visible)
  if (!s.visible) {
    hideToasts(v)
    return
  }
  const L = tuning.labels
  attr(v.mode, 'data-mode', MODE_NAMES[s.mode])
  flag(v.alive, 'data-alive', s.alive)
  text(v.modeText, L.modes[s.mode])
  cargo(v.cargo, s.cargoUsedT, s.cargoCapacityT)
  flag(v.cargoFull, 'data-full', s.cargoFull)
  credits(v.wallet, s.wallet)
  if (s.owned) text(v.lease, L.owned)
  else clock(v.lease, s.leaseLeft, 'LEASE ')
  flag(v.leaseWarn, 'data-warn', s.leaseWarn)
  attr(v.collections, 'data-level', s.collections === 2 ? 'repo' : s.collections === 1 ? 'notice' : 'none')
  text(v.collectionsText, s.collections === 2 ? L.collectionsRepo : s.collections === 1 ? L.collectionsNotice : '')

  renderNav(v, s, tuning)
  renderMarkers(v, s, tuning)
  renderScan(v, s, tuning)
  renderMining(v, s, tuning)
  renderSalvage(v, s, tuning)
  renderDock(v, s, tuning)
  renderToasts(v, s)
}

function renderNav(v: M2HudView, s: M2HudState, tuning: M2HudTuning): void {
  const nav = s.nav
  const has = nav.kind !== NAV.none && s.alive
  const onScreen = has && nav.point.onScreen
  flag(v.navOn, 'data-on', onScreen)
  flag(v.arrowOn, 'data-on', has && !onScreen)
  if (onScreen) {
    translate(v.navPos, nav.point.x, nav.point.y)
    scale(v.navFrame, nav.halfPx / 10)
    cruiseDistance(v.navDistance, nav.distance, nav.cruiseS, tuning)
  } else if (has) {
    translate(v.arrowPos, nav.point.edgeX, nav.point.edgeY)
    rotate(v.arrowHead, nav.point.angle * RAD_TO_DEG)
    cruiseDistance(v.arrowDistance, nav.distance, nav.cruiseS, tuning)
  }
}

function renderMarkers(v: M2HudView, s: M2HudState, tuning: M2HudTuning): void {
  for (let i = 0; i < v.stations.length; i++) {
    const c = v.stations[i]
    const m = s.stations[i]
    flag(c.on, 'data-on', m.on)
    if (!m.on) continue
    placeMarker(c, m)
    attr(c.state, 'data-state', m.contract && m.state === STATION_STATE.none ? 'contract' : STATION_STATE_NAMES[m.state])
    text(c.label, m.name)
    cruiseDistance(c.distance, m.distance, m.cruiseS, tuning)
  }
  for (let i = 0; i < v.contracts.length; i++) {
    const c = v.contracts[i]
    const m = s.contracts[i]
    flag(c.on, 'data-on', m.on)
    if (!m.on) continue
    placeMarker(c, m)
    attr(c.state, 'data-type', CONTRACT_TYPES[m.type])
    text(c.label, contractMarkerLabel(m.type, tuning))
    distance(c.distance, m.distance)
  }
}

/** A station or contract marker at its laid-out position (model layoutMarkers: safe rect, spread, declutter). */
function placeMarker(c: MarkerChannels, m: M2Marker): void {
  flag(c.edge, 'data-edge', m.edge)
  flag(c.flip, 'data-flip', m.flip)
  flag(c.full, 'data-text', m.full)
  translate(c.pos, m.sx, m.sy)
}

function renderScan(v: M2HudView, s: M2HudState, tuning: M2HudTuning): void {
  const scan = s.scan
  const on = s.mode !== TOOL.combat && (scan.scanning || scan.kind !== SCAN_KIND.none)
  flag(v.scanOn, 'data-on', on)
  if (!on) return
  attr(v.scanState, 'data-kind', scan.scanning ? 'scanning' : SCAN_KIND_NAMES[scan.kind])
  scaleX(v.scanProgress, scan.scanning ? scan.progress : scan.kind !== SCAN_KIND.none ? 1 : 0)
  if (scan.scanning && scan.kind === SCAN_KIND.none) {
    text(v.scanTitle, tuning.labels.scanning)
    text(v.scanValue, '')
    text(v.scanMode, '')
    text(v.scanTier, '')
    text(v.scanField, '')
  } else {
    text(v.scanTitle, scan.title)
    text(v.scanValue, scan.value)
    text(v.scanMode, scan.mode)
    text(v.scanTier, scan.tier)
    text(v.scanField, scan.kind === SCAN_KIND.rock ? scan.field : '')
  }
  for (let i = 0; i < v.rows.length; i++) {
    const r = v.rows[i]
    const rowOn = i < scan.rowCount
    flag(r.on, 'data-on', rowOn)
    if (!rowOn) continue
    flag(r.flag, 'data-flag', scan.rowFlag[i] === 1)
    text(r.label, scan.rowLabel[i])
    text(r.value, scan.rowValue[i])
  }
}

function renderMining(v: M2HudView, s: M2HudState, tuning: M2HudTuning): void {
  const m = s.mining
  const on = m.on && s.alive
  flag(v.miningOn, 'data-on', on)
  if (!on) return
  text(v.miningMode, !m.contact ? '' : m.mode === 1 ? tuning.labels.fracture : tuning.labels.chip)
  scaleX(v.heat, m.heat)
  attr(v.heatState, 'data-state', m.overheated ? 'overheat' : m.heat > 0.75 ? 'hot' : 'ok')
  const note = m.overheated ? tuning.labels.overheated : m.tooHard ? tuning.labels.tooHard : ''
  flag(v.miningNoteOn, 'data-on', note !== '')
  text(v.miningNote, note)
  flag(v.fractureOn, 'data-on', m.fracture)
  if (!m.fracture) return
  span(v.window, m.windowLeft, m.windowWidth)
  slide(v.needle, m.power)
  attr(v.needleBand, 'data-band', BAND_NAMES[m.band + 1])
  slide(v.mkLine, m.maxPower)
  scaleX(v.instability, m.instability)
  scaleX(v.progress, m.progress)
}

function renderSalvage(v: M2HudView, s: M2HudState, tuning: M2HudTuning): void {
  const sv = s.salvage
  const on = sv.on && s.alive
  flag(v.salvageOn, 'data-on', on)
  const anchors = on ? sv.anchorCount : 0
  for (let i = 0; i < v.anchorOn.length; i++) {
    const anchorOn = i < anchors
    flag(v.anchorOn[i], 'data-on', anchorOn)
    if (!anchorOn) continue
    translate(v.anchorPos[i], sv.anchorX[i], sv.anchorY[i])
    attr(v.anchorKind[i], 'data-kind', sv.anchorCoolant[i] === 1 ? (sv.anchorActive[i] === 1 ? 'coolant-active' : 'coolant') : sv.anchorActive[i] === 1 ? 'active' : 'anchor')
    arc(v.anchorArc[i], sv.anchorCut[i])
  }
  const parts = on ? sv.partCount : 0
  for (let i = 0; i < v.parts.length; i++) {
    const c = v.parts[i]
    const partOn = i < parts
    flag(c.on, 'data-on', partOn)
    if (!partOn) continue
    attr(c.state, 'data-state', PART_STATE_NAMES[sv.partState[i]])
    translate(c.pos, sv.partX[i], sv.partY[i])
    scale(c.frame, sv.partHalf[i] / 10)
    partValue(c.value, sv.partKnown[i] === 1, sv.partValueLow[i], sv.partValueHigh[i], tuning.labels.unknownValue)
    percentText(c.condition, sv.partCondition[i])
  }
  if (!on) return
  const L = tuning.labels
  const status = sv.status
  const statusText =
    status === SALVAGE_STATUS.tooLarge ? L.tooLarge : status === SALVAGE_STATUS.holdFull ? L.holdFull : status === SALVAGE_STATUS.outOfRange ? L.outOfRange : ''
  attr(v.salvageStatusKind, 'data-status', SALVAGE_STATUS_NAMES[status])
  text(v.salvageStatus, statusText)
  flag(v.tractorOn, 'data-on', sv.tractorOn)
  flag(v.cutOn, 'data-cutting', sv.beamOn && !sv.spilling && sv.cutProgress > 0)
  scaleX(v.cut, sv.cutProgress)
}

function renderDock(v: M2HudView, s: M2HudState, tuning: M2HudTuning): void {
  const d = s.dock
  const on = d.on && s.alive
  flag(v.dockOn, 'data-on', on)
  flag(v.reticleOn, 'data-on', on && d.guides)
  flag(v.noseOn, 'data-on', on && d.noseOn)
  flag(v.padOn, 'data-on', on && d.padOn)
  flag(v.promptOn, 'data-on', on && d.prompt !== DOCK_PROMPT.none)
  if (!on) return
  const L = tuning.labels
  attr(v.dockPhase, 'data-phase', DOCK_PHASE_NAMES[d.phase])
  text(v.dockPhaseText, d.phase === DOCK_PHASE.autodock ? L.autodock : d.phase === DOCK_PHASE.cleared ? L.cleared : d.phase === DOCK_PHASE.docked ? L.docked : '')
  if (d.phase === DOCK_PHASE.cleared || d.phase === DOCK_PHASE.autodock) clock(v.clearance, d.clearanceLeft, '')
  else text(v.clearance, '')
  flag(v.speedOver, 'data-over', d.overLimit)
  integer(v.speed, d.speed)
  integer(v.speedLimit, d.speedLimit)
  distance(v.padDistance, d.padDistance)
  // CL-2: the next step after clearance. The text is a model string compared by identity (no DOM write unless it changes).
  attr(v.promptRange, 'data-range', d.prompt === DOCK_PROMPT.far ? 'far' : 'in')
  text(v.promptText, d.promptText)
  if (d.guides) {
    flag(v.reticleAligned, 'data-aligned', d.aligned)
    rotate(v.rollBar, d.rollDeg)
    arc(v.holdArc, d.holdProgress)
  }
  if (d.noseOn) translate(v.nosePos, d.nose.x, d.nose.y)
  if (d.padOn) translate(v.padPos, d.pad.x, d.pad.y)
}

/**
 * The toasts are chips of the top-centre status stack (H11, D-617 §6; hud/status.ts): the stack places them,
 * newest on top, on show and hide only (no per-frame work).
 */
function renderToasts(v: M2HudView, s: M2HudState): void {
  for (let i = 0; i < v.toasts.length; i++) {
    const c = v.toasts[i]
    const t = s.toasts[i]
    const was = c.on.s === '1'
    flag(c.on, 'data-on', t.active)
    if (!t.active) {
      if (was) statusHide(v.status, c.on.node)
      continue
    }
    if (t.seq !== c.seq.a) {
      c.seq.a = t.seq
      text(c.text, t.text)
      text(c.speaker, t.speaker)
      attr(c.kind, 'data-kind', t.kind)
      // A fresh toast (also one reusing a shown slot) is the newest chip.
      statusShow(v.status, c.on.node)
    } else if (!was) statusShow(v.status, c.on.node)
    opacity(c.opacity, t.opacity)
  }
}

/** Takes every shown toast out of the stack while the M2 HUD is hidden; they come back when it shows. */
function hideToasts(v: M2HudView): void {
  for (let i = 0; i < v.toasts.length; i++) {
    const c = v.toasts[i]
    if (c.on.s !== '1') continue
    flag(c.on, 'data-on', false)
    statusHide(v.status, c.on.node)
  }
}

// ---- DOM --------------------------------------------------------------------------------------------------------

function markup(counts: M2HudCounts, tuning: M2HudTuning): string {
  let stations = ''
  for (let i = 0; i < counts.stations; i++) {
    stations +=
      `<div class="m2-station m2-abs" data-m2="station${i}" data-on="0" data-state="none" data-edge="0">` +
      `<svg class="m2-station-icon" viewBox="-10 -10 20 20" aria-hidden="true"><path d="M 0 -8 L 8 0 L 0 8 L -8 0 Z"/><circle r="2.2"/></svg>` +
      `<div class="m2-mlabel"><span data-m2="station${i}.label"></span> <b data-m2="station${i}.distance"></b></div></div>`
  }
  let contracts = ''
  for (let i = 0; i < counts.contracts; i++) {
    contracts +=
      `<div class="m2-contract m2-abs" data-m2="contract${i}" data-on="0" data-type="delivery" data-edge="0">` +
      `<svg class="m2-contract-icon" viewBox="-10 -10 20 20" aria-hidden="true"><path d="M -7 -7 H 7 V 7 H -7 Z M -3 0 H 3 M 0 -3 V 3"/></svg>` +
      `<div class="m2-mlabel"><span data-m2="contract${i}.label"></span> <b data-m2="contract${i}.distance"></b></div></div>`
  }
  let rows = ''
  for (let i = 0; i < SCAN_ROWS; i++) {
    rows += `<div class="m2-row" data-m2="row${i}" data-on="0" data-flag="0"><span data-m2="row${i}.label"></span><b data-m2="row${i}.value"></b></div>`
  }
  let anchors = ''
  for (let i = 0; i < ANCHOR_MARKERS; i++) {
    anchors +=
      `<svg class="m2-anchor m2-abs" data-m2="anchor${i}" data-on="0" data-kind="anchor" viewBox="-12 -12 24 24" aria-hidden="true">` +
      `<circle class="m2-anchor-ring" r="7"/><path class="m2-anchor-diamond" d="M 0 -9 L 9 0 L 0 9 L -9 0 Z"/>` +
      `<circle class="m2-anchor-arc" data-m2="anchor${i}.arc" r="10" pathLength="100" stroke-dasharray="100" transform="rotate(-90)" style="stroke-dashoffset:100"/></svg>`
  }
  let parts = ''
  for (let i = 0; i < PART_BRACKETS; i++) {
    parts +=
      `<div class="m2-part m2-abs" data-m2="part${i}" data-on="0" data-state="attached">` +
      `<svg class="m2-part-frame" data-m2="part${i}.frame" viewBox="-10 -10 20 20" aria-hidden="true">` +
      `<path d="M -10 -5 V -10 H -5 M 5 -10 H 10 V -5 M 10 5 V 10 H 5 M -5 10 H -10 V 5"/></svg>` +
      `<div class="m2-part-info"><span data-m2="part${i}.value"></span> <b data-m2="part${i}.condition"></b></div></div>`
  }
  let toasts = ''
  for (let i = 0; i < counts.toasts; i++) {
    toasts +=
      `<div class="m2-toast hud-chip" data-m2="toast${i}" data-on="0" data-kind="info">` +
      `<div class="m2-toast-speaker" data-m2="toast${i}.speaker"></div><div class="m2-toast-text" data-m2="toast${i}.text"></div></div>`
  }
  return `
  <div class="m2hud-root" data-m2="root" data-visible="0" data-mode="combat" data-alive="0">
    <div class="m2-layer">
      ${stations}${contracts}${anchors}${parts}
      <div class="m2-nav m2-abs" data-m2="nav" data-on="0">
        <svg class="m2-nav-frame" data-m2="nav.frame" viewBox="-10 -10 20 20" aria-hidden="true">
          <path d="M -10 -6 V -10 H -6 M 6 -10 H 10 V -6 M 10 6 V 10 H 6 M -6 10 H -10 V 6"/>
        </svg>
        <div class="m2-nav-dist" data-m2="nav.distance"></div>
      </div>
      <div class="m2-arrow m2-abs" data-m2="arrow" data-on="0">
        <svg class="m2-arrow-head" data-m2="arrow.head" viewBox="-14 -14 28 28" aria-hidden="true"><path d="M 0 -12 L 9 5 L 0 0 L -9 5 Z"/></svg>
        <div class="m2-arrow-dist" data-m2="arrow.distance"></div>
      </div>
      <svg class="m2-nose m2-abs" data-m2="nose" data-on="0" viewBox="-14 -14 28 28" aria-hidden="true"><path d="M -12 0 H -5 M 5 0 H 12 M 0 -12 V -5 M 0 5 V 12"/></svg>
      <svg class="m2-pad m2-abs" data-m2="pad" data-on="0" viewBox="-16 -16 32 32" aria-hidden="true"><rect x="-11" y="-11" width="22" height="22"/><path d="M -5 0 H 5 M 0 -5 V 5"/></svg>
    </div>
    <div class="m2-reticle" data-m2="reticle" data-on="0" data-aligned="0">
      <svg viewBox="-60 -60 120 120" aria-hidden="true">
        <circle class="m2-reticle-ring" r="46"/>
        <path class="m2-reticle-ticks" d="M -58 0 H -50 M 50 0 H 58 M 0 -58 V -50"/>
        <circle class="m2-hold-arc" data-m2="reticle.hold" r="52" pathLength="100" stroke-dasharray="100" transform="rotate(-90)" style="stroke-dashoffset:100"/>
        <g data-m2="reticle.roll" class="m2-roll"><path d="M -40 0 H -14 M 14 0 H 40 M 0 -40 V -30"/></g>
      </svg>
    </div>
    <div class="m2-top">
      <span class="m2-mode" data-m2="mode">${tuning.labels.modes[0]}</span>
      <span class="m2-sep"></span>
      <span class="m2-cargo" data-m2="cargo" data-full="0"></span>
      <span class="m2-sep"></span>
      <span class="m2-wallet" data-m2="wallet"></span>
      <span class="m2-sep"></span>
      <span class="m2-lease" data-m2="lease" data-warn="0"></span>
      <span class="m2-collections" data-m2="collections" data-level="none"></span>
    </div>
    <div class="m2-scan" data-m2="scan" data-on="0" data-kind="none">
      <div class="m2-scan-title" data-m2="scan.title"></div>
      <div class="m2-track"><span class="m2-fill" data-m2="scan.progress"></span></div>
      <div class="m2-scan-meta"><span data-m2="scan.mode"></span> <span data-m2="scan.tier"></span></div>
      <div class="m2-scan-meta" data-m2="scan.field"></div>
      <div class="m2-scan-value" data-m2="scan.value"></div>
      <div class="m2-rows">${rows}</div>
    </div>
    <div class="m2-panel m2-mining" data-m2="mining" data-on="0">
      <div class="m2-mining-mode" data-m2="mining.mode"></div>
      <div class="m2-fracture" data-m2="fracture" data-on="0">
        <div class="m2-powerbar">
          <span class="m2-window" data-m2="fracture.window"></span>
          <span class="m2-mk" data-m2="fracture.mk"></span>
          <span class="m2-needle" data-m2="fracture.needle" data-band="below"></span>
        </div>
        <div class="m2-meter"><span class="m2-meter-label">INST</span><span class="m2-track"><span class="m2-fill m2-fill-inst" data-m2="fracture.instability"></span></span></div>
        <div class="m2-meter"><span class="m2-meter-label">FRAC</span><span class="m2-track"><span class="m2-fill" data-m2="fracture.progress"></span></span></div>
      </div>
      <div class="m2-meter m2-heat" data-m2="heat" data-state="ok"><span class="m2-meter-label">HEAT</span><span class="m2-track"><span class="m2-fill m2-fill-heat" data-m2="heat.fill"></span></span></div>
      <div class="m2-note" data-m2="mining.note" data-on="0"></div>
    </div>
    <div class="m2-panel m2-salvage" data-m2="salvage" data-on="0" data-cutting="0">
      <div class="m2-meter"><span class="m2-meter-label">CUT</span><span class="m2-track"><span class="m2-fill" data-m2="salvage.cut"></span></span></div>
      <div class="m2-tractor" data-m2="salvage.tractor" data-on="0">${tuning.labels.tractorOn}</div>
      <div class="m2-note m2-salvage-status" data-m2="salvage.status" data-status="ok"></div>
    </div>
    <div class="m2-panel m2-dock" data-m2="dock" data-on="0" data-phase="space">
      <div class="m2-dock-head"><span class="m2-dock-phase" data-m2="dock.phase"></span> <span class="m2-dock-clearance" data-m2="dock.clearance"></span></div>
      <div class="m2-dock-speed" data-m2="dock.speedrow" data-over="0">
        <span class="m2-over-chevron" aria-hidden="true">&#9650;</span>
        <b data-m2="dock.speed">0</b><span class="m2-unit">/</span><span data-m2="dock.limit">0</span><span class="m2-unit">m/s</span>
      </div>
      <div class="m2-dock-pad"><span class="m2-meter-label">PAD</span> <span data-m2="dock.pad"></span></div>
      <div class="m2-dock-prompt" data-m2="dock.prompt" data-on="0" data-range="in"></div>
    </div>
    <div class="m2-toasts">${toasts}</div>
  </div>`
}

/**
 * Builds the M2 HUD inside `host` (`#hud`, after the M1 HUD's markup; the M1 HUD is left untouched) and returns
 * the elements the view writes. Init only.
 */
export function mountM2Hud(host: HTMLElement, counts: M2HudCounts, tuning: M2HudTuning): M2HudElements {
  const wrap = host.ownerDocument.createElement('div')
  wrap.className = 'm2hud'
  wrap.innerHTML = markup(counts, tuning)
  host.appendChild(wrap)
  host.setAttribute('data-mode', 'career')
  const make = (name: string): M2Node => {
    const el = wrap.querySelector<HTMLElement | SVGElement>(`[data-m2="${name}"]`)
    if (!el) throw new Error(`M2 HUD element ${name} missing`)
    return el
  }
  return buildM2HudElements(host, make, counts)
}
